import type { FilterItem, KeyedArray } from "@repo/models";

import { useMemo } from "react";

import type { idType } from ".";
import type { BranchFilterItem } from "../models/filterItem";

import { GridFiltersController } from "../api/gridFiltersController";
import { useFilterItemQuery } from "../api/queries";
import { BRANCHES, BRANDS, CHANNELS, MARKETERS, STATES } from "../api/queryKeys";

const emptySegment: ISegment = {};

export function useSegmentFilterCascaded<T = KeyedArray<FilterItem>>(
	segmentProp: ISegment,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	const {
		brandId,
		brandIds,
		channelId,
		channelIds,
		marketerId,
		marketerIds,
		stateId,
		stateIds,
		showInactiveOffices,
	} = segmentProp ?? emptySegment;
	const [brands, brandsLoaded] = useFilterItemQuery([BRANDS], GridFiltersController.getBrands);
	const [channels, channelsLoaded] = useFilterItemQuery([CHANNELS], GridFiltersController.getChannels);
	const [marketers, marketersLoaded] = useFilterItemQuery([MARKETERS], GridFiltersController.getMarketers);
	const [states, statesLoaded] = useFilterItemQuery([STATES], GridFiltersController.getStates);
	const [branches, branchesLoaded] = useFilterItemQuery<BranchFilterItem>(
		[BRANCHES],
		GridFiltersController.getBranches,
	);

	const branchesOnBrand = useMemo(
		() => filterBranches(branches?.data ?? [], { brandId, brandIds }, showInactiveOffices),
		[branches, brandId, brandIds, showInactiveOffices],
	);
	const branchesOnChannel = useMemo(
		() => filterBranches(branchesOnBrand, { channelId, channelIds }, showInactiveOffices),
		[branchesOnBrand, channelId, channelIds, showInactiveOffices],
	);
	const branchesOnMarketer = useMemo(
		() => filterBranches(branchesOnChannel, { marketerId, marketerIds }, showInactiveOffices),
		[branchesOnChannel, marketerId, marketerIds, showInactiveOffices],
	);
	const branchesOnState = useMemo(
		() => filterBranches(branchesOnMarketer, { stateId, stateIds }, showInactiveOffices),
		[branchesOnMarketer, stateId, stateIds, showInactiveOffices],
	);

	return useMemo(
		() =>
			({
				brands,
				channels: relevantItems(branchesOnBrand, channels, (b) => b.channelId, mapItems),
				marketers: relevantItems(branchesOnChannel, marketers, (b) => b.marketerId, mapItems),
				states: relevantItems(branchesOnMarketer, states, (b) => b.stateId, mapItems),
				branches: relevantItems(branchesOnState, branches, (b) => b.id, mapItems),
				brandsLoaded,
				channelsLoaded,
				marketersLoaded,
				statesLoaded,
				branchesLoaded,
			}) as ISegmentList<T>,
		[
			brands,
			channels,
			marketers,
			states,
			branches,
			branchesOnBrand,
			branchesOnChannel,
			branchesOnMarketer,
			branchesOnState,
			mapItems,
			brandsLoaded,
			channelsLoaded,
			marketersLoaded,
			statesLoaded,
			branchesLoaded,
		],
	);
}

function filterBranches(branches: BranchFilterItem[], filter: Partial<ISegment>, showInactiveOffices: boolean) {
	if (!branches.length) return branches;
	return branches
		.filter((b) => showInactiveOffices || (b.Active ?? true))
		.filter((b) => !filter.brandId || b.brandId === filter.brandId)
		.filter((b) => !filter.channelId || b.channelId === filter.channelId)
		.filter((b) => !filter.marketerId || b.marketerId === filter.marketerId)
		.filter((b) => !filter.stateId || b.stateId === filter.stateId)
		.filter((b) => !filter.brandIds?.length || filter.brandIds.includes(b.brandId))
		.filter((b) => !filter.channelIds?.length || filter.channelIds.includes(b.channelId))
		.filter((b) => !filter.marketerIds?.length || filter.marketerIds.includes(b.marketerId))
		.filter((b) => !filter.stateIds?.length || filter.stateIds.includes(b.stateId));
}

/** The items any of `branches` points at; every item when no branch matched. */
function relevantItems<T>(
	branches: BranchFilterItem[],
	allItems: KeyedArray<FilterItem>,
	selector: (b: BranchFilterItem) => number,
	mapItems?: (i: KeyedArray<FilterItem>) => T,
) {
	if (!branches.length) return mapItems ? mapItems(allItems) : allItems;
	const groups = branches.groupBy(selector);
	const items = allItems.filter((i) => groups.has(i.id));
	return mapItems ? mapItems(items) : items;
}

export interface ISegment {
	brandId?: idType;
	channelId?: idType;
	marketerId?: idType;
	stateId?: idType;
	branchId?: idType;

	showInactiveOffices?: boolean;

	brandIds?: idType[];
	channelIds?: idType[];
	marketerIds?: idType[];
	stateIds?: idType[];
	branchIds?: idType[];

	assignedUserIds?: idType[];
}

export interface ISegmentList<T = KeyedArray<FilterItem>> {
	brands: T;
	channels: T;
	marketers: T;
	states: T;
	branches: T;
	brandsLoaded: boolean;
	channelsLoaded: boolean;
	marketersLoaded: boolean;
	statesLoaded: boolean;
	branchesLoaded: boolean;
}
